import React, { useState, useEffect } from 'react';
import { fetchEmployeeDetails, fetchUserProfilePicture } from 'utils/api'; // functions for API calls
import { useCustomToast } from 'utils/toastUtils';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { LOCAL_STORAGE_KEYS, formatDate } from 'utils/constants';
import {
  // Chakra UI components for layout and design
  Box,
  Button,
  Flex,
  Heading,
  Text,
  VStack,
  Avatar,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Icon,
  useToast,
  Grid,
  GridItem,
} from '@chakra-ui/react';
import {
  // Icon imports
  FaEnvelope,
  FaTransgender,
  FaMapMarkerAlt,
  FaNetworkWired,
  FaBirthdayCake,
  FaHeart,
  FaCalendar,
  FaUserTie,
  FaUsers,
  FaIdBadge,
  FaHeartbeat,
  FaBarcode,
  FaUserTag,
  FaUserCheck,
} from 'react-icons/fa';

import { MdPhone, MdContactPhone } from 'react-icons/md';
import { useLoader } from "contexts/LoaderContext";

export default function UserDetailsPage({ emp_code, onBack }) {
  // ==================== STATE VARIABLES ====================
  const showToast = useCustomToast(); // Custom hook to show styled toasts
  const { showGlobalLoader, hideGlobalLoader } = useLoader();
  const [activeTab, setActiveTab] = useState(0); // State to manage currently active tab
  const [userDetails, setUserDetails] = useState(null);
  const toast = useToast(); // Chakra UI toast instance
  const [initialUserDetails, setInitialUserDetails] = useState(null); // Stores initial user data
  const [profilePhoto, setProfilePhoto] = useState(null); // Holds profile picture

  // ==================== EFFECT HOOKS ====================
  /**
       Effect: Fetch and load user details on component mount 
       Retrieves employee data from API and formats it for display.
       Handles error states and shows appropriate toast notifications.
       */

  useEffect(() => {
    //function to load user details from API
    const loadUserDetails = async () => {
      const roleId = localStorage.getItem(LOCAL_STORAGE_KEYS.ROLE_ID);
      if (!emp_code || !roleId) return; // Early return if required data is missing
      showGlobalLoader();
      try {
        // Fetch employee data from API
        const response = await fetchEmployeeDetails(emp_code, roleId);
        const { employeeData, roletitle } = response;
        // Format API response data for component consumption
        const formattedData = {
          name: employeeData.username,
          emailId: employeeData.email_id,
          phoneNo: employeeData.phoneNo,
          location: employeeData.location,
          dateOfBirth: employeeData.dateofbirth,
          gender: employeeData.gender,
          maritalStatus: employeeData.maritalStatus,
          emgContactName: employeeData.emg_ContactName,
          emgContactPhoneNo: employeeData.emg_ContactPhoneNo,
          emgContactRelation: employeeData.emg_ContactRelation,
          designation: employeeData.designation,
          isBillable: employeeData.isBillable,
          employmentType: employeeData.employmentType,
          employmentType_Id: employeeData.employmentType_Id,
          departmentName: employeeData.departmentName,
          teamName: employeeData.teamName,
          reportingTo: employeeData.reportingTo,
          joiningDate: employeeData.joiningDate,
          emp_code: employeeData.emp_code,
          roleTitle: roletitle,
        };

        setUserDetails(formattedData); // Update state with formatted data
        setInitialUserDetails(formattedData);
      } catch (error) {
        console.error('Error fetching Employee details:', error);

        showToast(
          'error',
          'Unable to load Employee details. Please try again later.',
        );
      }finally {
      hideGlobalLoader();
    }
    };

    loadUserDetails();
  }, [emp_code, toast]);

  /**
   Effect: Disable page scrolling while component is mounted
  Prevents background scrolling for better UX when viewing profile. 
  Cleanup function restores normal scrolling on component unmount.
 */
  useEffect(() => {
    // Disable scroll on the body
    document.body.style.overflow = 'hidden';

    // Clean up: Restore the scroll when the component unmounts
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  // Effect: Fetch user profile picture after user details are loaded
  useEffect(() => {
    const loadProfilePicture = async () => {
      if (!userDetails || !userDetails.emp_code) return; // Early return if user details aren't loaded yet

      const imageUrl = await fetchUserProfilePicture(userDetails.emp_code);
      if (imageUrl) setProfilePhoto(imageUrl);
    };

    loadProfilePicture();
  }, [userDetails]);

  // ==================== UTILITY COMPONENTS ====================
  const InfoRow = ({ icon: IconComponent, label, value, children }) => (
    <Flex
      align="center"
      py={3}
      borderBottom="1px solid"
      borderColor="gray.200"
      textAlign="left"
      minH="80px"
    >
      <Icon as={IconComponent} color="gray.500" mr={4} boxSize={5} />
      <Flex width="full" flexDirection="column" align="flex-start">
        <Text fontWeight="medium" color="gray.600" mb={1}>
          {label}
        </Text>
        {value ? (
          <Text fontWeight="semibold" color="gray.800">
            {value}
          </Text>
        ) : (
          children || <Text color="gray.400">NA</Text>
        )}
      </Flex>
    </Flex>
  );
  // ==================== MAIN COMPONENT RENDER ====================
  return (
    <Flex
      width="100%"
      height="94vh"
      bg="white"
      align="center"
      justify="center"
      p={6}
      overflow="hidden"
    >
      <Box
        width="100%"
        minH="90vh"
        maxWidth="1000px"
        bg="white"
        border="1px solid"
        borderColor="gray.100"
        borderRadius="lg"
        padding='30px 1px 0px 1px'
      >
        <ToastContainer /> {/* Toast notification container */}
        {/* ==================== HEADER SECTION ==================== */}
        <Flex
          bg="gray.100"
          color="gray.900"
          p={6}
          align="center"
          borderBottom="1px solid"
          borderColor="gray.100"
        >
          <Box position="relative" width="fit-content">
            <Avatar
              size="xl"
              name={userDetails?.name}
              src={profilePhoto || undefined}
              bg="gray.200"
              color="gray.800"
              mr={6}
              borderWidth={2}
              borderColor="gray.600"
              cursor="pointer"
            />
          </Box>

          <VStack align="flex-start" spacing={1} flex={1}>
            <Heading size="lg" color="gray.800">
              {userDetails?.name || 'Loading...'}
            </Heading>

            <Text color="gray.600">{userDetails?.jobTitle}</Text>
          </VStack>
          <Flex gap={4} justify="flex-end" mt={3}>
            <Button className="main common-button xsmall" onClick={onBack}>
              Back
            </Button>
          </Flex>
        </Flex>
        {/* ==================== TABBED CONTENT SECTION ==================== */}
        <Tabs
          variant="line"
          colorScheme="gray"
          px={6}
          onChange={(index) => setActiveTab(index)}
        >
          {/* Tab navigation */}
          <TabList>
            <Tab
              _selected={{
                borderBottomColor: 'gray.600',
                color: 'gray.800',
                fontWeight: 'semibold',
              }}
            >
              Professional Details
            </Tab>
            <Tab
              _selected={{
                borderBottomColor: 'gray.600',
                color: 'gray.800',
                fontWeight: 'semibold',
              }}
            >
              Personal Details
            </Tab>
          </TabList>
          <TabPanels>
            {/* ==================== PROFESSIONAL DETAILS TAB ==================== */}
            <TabPanel>
              <Grid
                templateColumns="repeat(2, 1fr)"
                gap={4}
                alignItems="stretch"
              >
                <GridItem>
                  <InfoRow
                    icon={FaBarcode}
                    label="Employee Code"
                    value={`E0${userDetails?.emp_code}`}
                  />
                </GridItem>

                <GridItem>
                  <InfoRow
                    icon={FaIdBadge}
                    label="Designation"
                    value={userDetails?.designation}
                  />
                </GridItem>
                <GridItem>
                  <InfoRow
                    icon={FaUserCheck}
                    label="Billable"
                    value={userDetails?.isBillable ? 'Yes' : 'No'}
                  />
                </GridItem>
                <GridItem>
                  <InfoRow
                    icon={FaUserTag}
                    label="Employment Type"
                    value={userDetails?.employmentType}
                  />
                </GridItem>

                <GridItem>
                  <InfoRow
                    icon={FaNetworkWired}
                    label="Department"
                    value={userDetails?.departmentName}
                  />
                </GridItem>
                <GridItem>
                  <InfoRow
                    icon={FaUsers}
                    label="Team"
                    value={userDetails?.teamName}
                  />
                </GridItem>
                <GridItem>
                  <InfoRow
                    icon={FaUserTie}
                    label="Reporting To"
                    value={userDetails?.reportingTo}
                  />
                </GridItem>
                <GridItem>
                  <InfoRow
                    icon={FaCalendar}
                    label="Joining Date"
                    value={formatDate(userDetails?.joiningDate)}
                  />
                </GridItem>
              </Grid>
            </TabPanel>
            {/* ==================== PERSONAL DETAILS TAB ==================== */}
            <TabPanel>
              <Grid
                templateColumns="repeat(2, 1fr)"
                gap={4}
                mt={4}
                alignItems="stretch"
              >
                <GridItem>
                  <InfoRow
                    icon={FaEnvelope}
                    label="Email"
                    value={userDetails?.emailId}
                  />
                </GridItem>
                <GridItem>
                  <InfoRow
                    icon={MdPhone}
                    label="Phone"
                    value={userDetails?.phoneNo}
                  />
                </GridItem>
                <GridItem>
                  <InfoRow
                    icon={FaMapMarkerAlt}
                    label="Location"
                    value={
                      userDetails?.location === 'NA' ||
                      userDetails?.location === '0' ||
                      !userDetails?.location
                        ? null // This triggers the placeholder styling[NA]
                        : userDetails?.location
                    }
                  />
                </GridItem>
                <GridItem>
                  <InfoRow
                    icon={FaBirthdayCake}
                    label="Date of Birth"
                    value={formatDate(userDetails?.dateOfBirth)}
                  />
                </GridItem>
                <GridItem>
                  <InfoRow
                    icon={FaTransgender}
                    label="Gender"
                    value={userDetails?.gender}
                  />
                </GridItem>
                <GridItem>
                  <InfoRow
                    icon={FaHeart}
                    label="Marital Status"
                    value={
                      userDetails?.maritalStatus === 'NA' ||
                      userDetails?.maritalStatus === '0' ||
                      !userDetails?.maritalStatus
                        ? null // This triggers the placeholder styling[NA]
                        : userDetails?.maritalStatus
                    }
                  />
                </GridItem>
                <GridItem>
                  <InfoRow
                    icon={MdContactPhone}
                    label="Emergency Contact"
                    value={(() => {
                      const name =
                        userDetails?.emgContactName &&
                        userDetails.emgContactName !== 'NA' &&
                        userDetails.emgContactName !== '0'
                          ? userDetails.emgContactName
                          : '';

                      const relation =
                        userDetails?.emgContactRelation &&
                        userDetails.emgContactRelation !== 'NA' &&
                        userDetails.emgContactRelation !== '0'
                          ? userDetails.emgContactRelation
                          : '';

                      const phone =
                        userDetails?.emgContactPhoneNo &&
                        userDetails.emgContactPhoneNo !== 'NA' &&
                        userDetails.emgContactPhoneNo !== '0'
                          ? userDetails.emgContactPhoneNo
                          : '';

                      //  Formatting rules
                      if (name && relation && phone)
                        return `${name} (${relation}) • ${phone}`;
                      if (name && relation) return `${name} (${relation})`;
                      if (name && phone) return `${name} • ${phone}`;
                      if (relation && phone) return `${relation} • ${phone}`;
                      if (name) return name;
                      if (relation) return relation;
                      if (phone) return phone;

                      return null;
                    })()}
                  />
                </GridItem>
              </Grid>
            </TabPanel>
          </TabPanels>
        </Tabs>
        {/* ==================== FOOTER SECTION ==================== */}
        <Flex
          justify="flex-end"
          p={6}
          borderTop="1px solid"
          borderColor="gray.100"
        ></Flex>
      </Box>
    </Flex>
  );
}
